// src/share/ProfileSharePreview.jsx
// Platform-specific profile share preview frames for the Sharing Center.
// Follows the same pattern as PublishPreviewModal.jsx but renders a
// ProfileShareCard (teammate profile data) instead of ImageComposite.

import React from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

// ─── Helpers (mirrored from PublishPreviewModal) ────────────────────────────
function makeHandle(name) {
  if (!name) return 'yourbusiness';
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 20);
}

function makeInitials(name) {
  if (!name) return 'Biz';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// ─── Star rating row ────────────────────────────────────────────────────────
function Stars({ avg, count }) {
  if (!avg && !count) return null;
  const score = (avg || 0).toFixed(2);
  const full = Math.floor(avg || 0);
  const half = (avg || 0) - full >= 0.25;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex items-center">
        {Array.from({ length: full }).map((_, i) => (
          <span key={`f${i}`} className="text-amber-400" style={{ fontSize: 'inherit' }}>&#9733;</span>
        ))}
        {half && <span className="text-amber-400" style={{ fontSize: 'inherit' }}>&#9733;</span>}
        {Array.from({ length: empty }).map((_, i) => (
          <span key={`e${i}`} className="text-gray-300" style={{ fontSize: 'inherit' }}>&#9733;</span>
        ))}
      </div>
      <div className="font-bold text-gray-800 mt-0.5" style={{ fontSize: 'inherit' }}>{score}</div>
      {count > 0 && <div className="text-gray-400" style={{ fontSize: '0.85em' }}>{count} review{count === 1 ? '' : 's'}</div>}
    </div>
  );
}

// ─── Profile photo circle ───────────────────────────────────────────────────
function ProfilePhoto({ src, size = 64 }) {
  return (
    <div
      className="rounded-full overflow-hidden border-2 border-[#00aef7]/30 bg-gray-100 shrink-0"
      style={{ width: size, height: size }}
    >
      {src ? (
        <img
          src={`${API_URL}${src}`}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-400 text-[10px]">
          No Image
        </div>
      )}
    </div>
  );
}

// ─── eHandshake branding strip ──────────────────────────────────────────────
function BrandingStrip({ light = false }) {
  return (
    <div className={`flex items-center justify-center py-1.5 ${light ? 'text-white/70' : 'text-[#00aef7]/60'}`}>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.05em' }}>Shared through eHandshake</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ProfileShareCard — the visual "image" that sits inside each platform frame
// ═══════════════════════════════════════════════════════════════════════════

function ProfileShareCard({ member, aspect = '1.91/1', showContact = false }) {
  const name = [member.u_first_name, member.u_last_name].filter(Boolean).join(' ') || 'Unknown';
  const title = member.u_job_title || '';
  const bizName = member.b_name || '';
  const industry = member.b_business_industry || '';
  const location = [member.b_city, member.b_state].filter(Boolean).join(', ');
  const photo = member.u_profile_picture || '';
  const avgRating = member.rating_avg ? Number(member.rating_avg) : 0;
  const ratingCount = member.rating_count ? Number(member.rating_count) : 0;

  // Vertical layout for square & 9/16
  const isVertical = aspect === '1/1' || aspect === '9/16';
  const isTall = aspect === '9/16';

  return (
    <div
      className="relative w-full overflow-hidden flex flex-col"
      style={{
        aspectRatio: aspect,
        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
      }}
    >
      {/* Top accent bar */}
      <div className="h-1 shrink-0" style={{ background: 'linear-gradient(90deg, #00aef7, #0077cc)' }} />

      {isVertical ? (
        /* ─── Vertical layout (Instagram 1:1, TikTok 9:16) ─── */
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-3 text-center">
          <ProfilePhoto src={photo} size={isTall ? 80 : 64} />
          <div className="mt-2 space-y-0.5">
            <div className="font-bold text-gray-900" style={{ fontSize: isTall ? 14 : 13 }}>{name}</div>
            {title && <div className="text-gray-600" style={{ fontSize: isTall ? 11 : 10 }}>{title}</div>}
            {bizName && <div className="font-semibold text-[#00aef7]" style={{ fontSize: isTall ? 11 : 10 }}>{bizName}</div>}
            {industry && <div className="text-gray-500" style={{ fontSize: isTall ? 10 : 9 }}>{industry}</div>}
            {location && (
              <div className="text-gray-400 flex items-center justify-center gap-0.5" style={{ fontSize: isTall ? 10 : 9 }}>
                <span>&#128205;</span> {location}
              </div>
            )}
            {avgRating > 0 && (
              <div className="pt-1 flex justify-center" style={{ fontSize: isTall ? 11 : 10 }}>
                <Stars avg={avgRating} count={ratingCount} />
              </div>
            )}
            {showContact && (
              <div className="pt-1.5 space-y-0.5 text-gray-500" style={{ fontSize: 9 }}>
                {(member.b_phone || member.u_phone_number) && <div>&#128222; {member.b_phone || member.u_phone_number}</div>}
                {(member.b_email || member.u_email) && <div>&#9993; {member.b_email || member.u_email}</div>}
                {member.b_website && <div>&#127760; {member.b_website}</div>}
                {member.b_show_address && member.b_address && <div>&#127968; {member.b_address}</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ─── Horizontal layout (Facebook 1.91:1, Twitter 16:9) ─── */
        <div className="flex-1 flex items-center px-4 py-3 gap-4">
          <ProfilePhoto src={photo} size={60} />
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="font-bold text-gray-900 text-[13px] truncate">{name}</div>
            {title && <div className="text-gray-600 text-[10px] truncate">{title}</div>}
            {bizName && <div className="font-semibold text-[#00aef7] text-[10px] truncate">{bizName}</div>}
            {industry && <div className="text-gray-500 text-[9px] truncate">{industry}</div>}
            {location && (
              <div className="text-gray-400 text-[9px] flex items-center gap-0.5">
                <span>&#128205;</span> {location}
              </div>
            )}
            {showContact && (
              <div className="pt-1 space-y-0.5 text-gray-500 text-[9px]">
                {(member.b_phone || member.u_phone_number) && <div>&#128222; {member.b_phone || member.u_phone_number}</div>}
                {(member.b_email || member.u_email) && <div>&#9993; {member.b_email || member.u_email}</div>}
                {member.b_website && <div>&#127760; {member.b_website}</div>}
                {member.b_show_address && member.b_address && <div>&#127968; {member.b_address}</div>}
              </div>
            )}
          </div>
          {avgRating > 0 && (
            <div className="shrink-0 text-[10px] flex items-center">
              <Stars avg={avgRating} count={ratingCount} />
            </div>
          )}
        </div>
      )}

      {/* Bottom branding */}
      <div className="shrink-0">
        <BrandingStrip />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Platform Frame Components
// ═══════════════════════════════════════════════════════════════════════════

function ProfileFacebookFrame({ member, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">Just now &middot; &#127760;</div>
        </div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="10" cy="4" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="10" cy="16" r="2"/></svg>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <ProfileShareCard member={member} aspect="1.91/1" showContact />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5"><span className="text-[12px]">&#128077;</span><span className="text-[12px]">&#10084;&#65039;</span><span>24</span></div>
        <div>3 comments &middot; 2 shares</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128077; Like</div>
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128172; Comment</div>
        <div className="flex-1 py-1 text-[11px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#8599;&#65039; Share</div>
      </div>
    </div>
  );
}

function ProfileInstagramFrame({ member, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[300px] mx-auto">
      <div className="px-2.5 py-1.5 flex items-center gap-2 border-b border-gray-100">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-400 p-[2px]">
          <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[8px] font-bold text-gray-700">{biz.initials}</div>
        </div>
        <div className="flex-1 min-w-0"><div className="text-[11px] font-semibold text-gray-900">{biz.handle}</div></div>
        <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><circle cx="4" cy="10" r="2"/><circle cx="10" cy="10" r="2"/><circle cx="16" cy="10" r="2"/></svg>
      </div>
      <ProfileShareCard member={member} aspect="1/1" showContact />
      <div className="px-2.5 py-1.5 flex items-center gap-3">
        <span className="text-[15px]">&#129293;</span>
        <span className="text-[15px]">&#128172;</span>
        <span className="text-[15px]">&#128228;</span>
        <span className="ml-auto text-[15px]">&#128278;</span>
      </div>
      <div className="px-2.5 text-[11px] font-semibold text-gray-900">127 likes</div>
      <div className="px-2.5 pb-2.5 pt-0.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap"><span className="font-semibold">{biz.handle} </span>{caption}</p>
      </div>
    </div>
  );
}

function ProfileYouTubeFrame({ member, caption, biz }) {
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden max-w-[210px] mx-auto" style={{ aspectRatio: '9/16' }}>
      <div className="relative w-full h-full">
        <ProfileShareCard member={member} aspect="9/16" showContact />
        {/* Gradient overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        {/* Sidebar engagement icons */}
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128077;</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128078;</div>
            <span className="text-white text-[8px] mt-0.5">Dislike</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128172;</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128228;</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
        {/* YouTube Shorts badge */}
        <div className="absolute top-2 left-2 bg-red-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">Shorts</div>
      </div>
    </div>
  );
}

function ProfileTwitterFrame({ member, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1 flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-white text-[9px] font-bold shrink-0">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[11px] font-bold text-gray-900">{biz.name}</span>
            <span className="text-[10px] text-gray-500">@{biz.handle} &middot; Just now</span>
          </div>
          <p className="text-[11px] text-gray-900 leading-snug whitespace-pre-wrap mt-0.5">{caption}</p>
        </div>
      </div>
      <div className="px-2.5 pb-1">
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <ProfileShareCard member={member} aspect="16/9" showContact />
        </div>
      </div>
      <div className="px-8 py-1.5 flex items-center justify-between text-gray-500 text-[11px]">
        <span className="flex items-center gap-0.5">&#128172; <span>5</span></span>
        <span className="flex items-center gap-0.5">&#128260; <span>12</span></span>
        <span className="flex items-center gap-0.5">&#129293; <span>47</span></span>
        <span className="flex items-center gap-0.5">&#128228;</span>
      </div>
    </div>
  );
}

function ProfileTikTokFrame({ member, caption, biz }) {
  return (
    <div className="bg-black rounded-2xl shadow-lg overflow-hidden max-w-[210px] mx-auto" style={{ aspectRatio: '9/16' }}>
      <div className="relative w-full h-full">
        <ProfileShareCard member={member} aspect="9/16" showContact />
        {/* Gradient overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-2" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))' }}>
          <div className="text-white text-[11px] font-bold mb-0.5">@{biz.handle}</div>
          <p className="text-white/90 text-[10px] leading-snug whitespace-pre-wrap">{caption}</p>
        </div>
        {/* Sidebar engagement icons */}
        <div className="absolute right-1.5 bottom-20 flex flex-col items-center gap-3">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#129293;</div>
            <span className="text-white text-[8px] mt-0.5">1.2K</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128172;</div>
            <span className="text-white text-[8px] mt-0.5">83</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-[11px]">&#128228;</div>
            <span className="text-white text-[8px] mt-0.5">Share</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileEmailFrame({ member, caption, biz }) {
  const name = [member.u_first_name, member.u_last_name].filter(Boolean).join(' ') || 'Unknown';
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      {/* Email header */}
      <div className="px-3 pt-2.5 pb-2 border-b border-gray-200 bg-gray-50">
        <div className="text-[11px] text-gray-500 mb-1">
          <span className="font-semibold text-gray-700">From:</span> {biz.name}
        </div>
        <div className="text-[11px] text-gray-500 mb-1.5">
          <span className="font-semibold text-gray-700">Subject:</span> Check out {name} on eHandshake
        </div>
      </div>
      {/* Email body */}
      <div className="px-3 py-2">
        <p className="text-[11px] text-gray-800 leading-snug mb-2">{caption}</p>
      </div>
      <div className="mx-3 mb-3 rounded-lg overflow-hidden border border-gray-200">
        <ProfileShareCard member={member} aspect="4/3" showContact />
      </div>
      {/* View profile link */}
      <div className="px-3 pb-3 text-center">
        <span className="inline-block px-4 py-1.5 bg-[#00aef7] text-white text-[11px] font-semibold rounded-md">
          View Full Profile
        </span>
      </div>
    </div>
  );
}

function ProfileLinkedInFrame({ member, caption, biz }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-[340px] mx-auto">
      <div className="px-2.5 pt-2 pb-1.5 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-[9px] font-bold">{biz.initials}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-900">{biz.name}</div>
          <div className="text-[10px] text-gray-500">{biz.location || 'Local Business'}</div>
          <div className="text-[10px] text-gray-400">Just now &middot; &#127760;</div>
        </div>
      </div>
      <div className="px-2.5 pb-1.5">
        <p className="text-[11px] text-gray-800 leading-snug whitespace-pre-wrap">{caption}</p>
      </div>
      <ProfileShareCard member={member} aspect="1.91/1" showContact />
      <div className="px-2.5 py-1 flex items-center justify-between text-[10px] text-gray-500 border-t border-gray-100">
        <div className="flex items-center gap-0.5"><span className="text-[12px]">&#128077;</span><span className="text-[12px]">&#128161;</span><span className="text-[12px]">&#10084;&#65039;</span><span className="ml-1">48</span></div>
        <div>6 comments</div>
      </div>
      <div className="px-1.5 py-0.5 border-t border-gray-200 flex">
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128077; Like</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128172; Comment</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128260; Repost</div>
        <div className="flex-1 py-1 text-[10px] text-gray-600 font-medium flex items-center justify-center gap-0.5">&#128228; Send</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ProfileShareModal — click-to-share modal (social platforms + email)
// ═══════════════════════════════════════════════════════════════════════════

const PLATFORM_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  twitter: 'X / Twitter',
  tiktok: 'TikTok',
  email: 'Email',
};

function ProfileShareModal({ member, platform, biz, socialConnections, fetchSocialStatus, onClose, onShare }) {
  const [caption, setCaption] = React.useState('I fully recommend this company. We have used their services.');
  const [emailTo, setEmailTo] = React.useState('');
  const [emailSubject, setEmailSubject] = React.useState(() => {
    const name = [member?.u_first_name, member?.u_last_name].filter(Boolean).join(' ') || 'a teammate';
    return `Check out ${name} on eHandshake`;
  });
  const [sending, setSending] = React.useState(false);
  const [connecting, setConnecting] = React.useState(false);
  const [postError, setPostError] = React.useState('');
  const oauthPopupRef = React.useRef(null);
  const postAfterConnectRef = React.useRef(false);

  // Listen for OAuth popup close → refresh status → auto-post if pending
  React.useEffect(() => {
    if (!connecting) return;
    const interval = setInterval(() => {
      if (oauthPopupRef.current?.closed) {
        clearInterval(interval);
        setConnecting(false);
        if (fetchSocialStatus) {
          fetchSocialStatus().then(() => {
            if (postAfterConnectRef.current) {
              postAfterConnectRef.current = false;
              doPost();
            }
          });
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [connecting]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!member || !platform) return null;

  const FrameComp = PROFILE_FRAME_MAP[platform];
  if (!FrameComp) return null;

  const isEmail = platform === 'email';
  const platformLabel = PLATFORM_LABELS[platform] || platform;
  const memberName = [member.u_first_name, member.u_last_name].filter(Boolean).join(' ') || 'Unknown';
  const conn = socialConnections?.[platform];
  const isConnected = conn?.connected;

  const doPost = async () => {
    setSending(true);
    setPostError('');
    try {
      if (onShare) {
        await onShare({ member, platform, caption, emailTo, emailSubject });
      }
      onClose();
    } catch (err) {
      console.error('[ProfileShareModal] share error:', err);
      setPostError(err.message || 'Failed to share. Please try again.');
      if (fetchSocialStatus) fetchSocialStatus(); // refresh connection status in case token expired
    }
    setSending(false);
  };

  const handlePost = () => {
    setPostError('');
    if (isEmail || isConnected) {
      doPost();
    } else {
      // Open OAuth popup
      postAfterConnectRef.current = true;
      setConnecting(true);
      const w = 600, h = 700;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      oauthPopupRef.current = window.open(
        `${API_URL}/api/social/${platform}/connect`,
        'social-connect',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center pointer-events-auto" style={{ zIndex: 100000 }}>
      <div
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden relative"
        style={{ width: isEmail ? 560 : 440, maxWidth: '94vw', maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800">
              {isEmail ? `Email ${memberName}'s Profile` : `Share ${memberName} on ${platformLabel}`}
            </h3>
            {!isEmail && isConnected && (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    const btn = e.currentTarget;
                    if (btn.dataset.confirm !== 'yes') {
                      btn.dataset.confirm = 'yes'; btn.textContent = 'Sure?';
                      btn.className = 'ml-1 text-[10px] text-red-500 font-semibold underline transition-colors';
                      setTimeout(() => { btn.dataset.confirm = ''; btn.textContent = 'Disconnect'; btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors'; }, 3000);
                      return;
                    }
                    btn.disabled = true; btn.textContent = 'Disconnecting…';
                    try {
                      await fetch(`${API_URL}/api/social/${platform}/disconnect`, { method: 'DELETE', credentials: 'include' });
                      if (fetchSocialStatus) fetchSocialStatus();
                    } catch (err) { console.error('disconnect error:', err); }
                    finally { btn.disabled = false; btn.dataset.confirm = ''; btn.textContent = 'Disconnect'; btn.className = 'ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors'; }
                  }}
                  className="ml-1 text-[10px] text-gray-400 hover:text-red-500 underline transition-colors"
                >Disconnect</button>
              </span>
            )}
            {!isEmail && !isConnected && (
              <span className="text-[10px] text-gray-400">Not connected</span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none" title="Close">&#10005;</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">

          {isEmail ? (
            /* ─── EMAIL FORM (matches ReviewRequestModal pattern) ─── */
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message:</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={4}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm"
                />
              </div>
              {/* Email preview */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">Preview</label>
                <div className="flex justify-center">
                  <FrameComp member={member} caption={caption} biz={biz} />
                </div>
              </div>
            </div>
          ) : (
            /* ─── SOCIAL PLATFORM LAYOUT ─── */
            <div className="p-5 space-y-4">
              {/* Caption input — above preview (matches Facebook/YouTube/Twitter convention) */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Your message</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={3}
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-[#00aef7] focus:outline-none text-sm resize-none"
                  placeholder="Write something about this teammate..."
                />
              </div>
              {/* Platform preview */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">{platformLabel} Preview</label>
                <div className="flex justify-center">
                  <FrameComp member={member} caption={caption} biz={biz} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Connecting / Posting overlay */}
        {(connecting || sending) && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-50 rounded-xl">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#00aef7] rounded-full animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-600">
              {connecting ? `Connecting to ${platformLabel}…` : `Posting to ${platformLabel}…`}
            </p>
            <p className="text-xs text-gray-400 mt-1">Please wait</p>
          </div>
        )}

        {/* Footer with action buttons */}
        <div className="shrink-0 px-5 py-3 border-t border-gray-200 bg-gray-50">
          {postError && <p className="text-xs text-red-500 mb-2">{postError}</p>}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              disabled={sending || connecting}
            >
              Cancel
            </button>
            <button
              onClick={handlePost}
              disabled={sending || connecting || (isEmail && !emailTo.trim())}
              className={`px-5 py-2 text-white text-sm font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isConnected || isEmail ? 'bg-[#00aef7] hover:bg-[#0095d6]' : 'bg-orange-500 hover:bg-orange-600'
              }`}
            >
              {sending
                ? (isEmail ? 'Sending...' : 'Posting...')
                : isEmail
                  ? 'Send Email'
                  : isConnected
                    ? `Post to ${platformLabel}`
                    : `Connect & Post to ${platformLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Exports
// ═══════════════════════════════════════════════════════════════════════════

export const PROFILE_FRAME_MAP = {
  facebook: ProfileFacebookFrame,
  instagram: ProfileInstagramFrame,
  youtube: ProfileYouTubeFrame,
  linkedin: ProfileLinkedInFrame,
  twitter: ProfileTwitterFrame,
  tiktok: ProfileTikTokFrame,
  email: ProfileEmailFrame,
};

export { makeHandle, makeInitials, ProfileShareModal };
